import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Spinner,
} from "reactstrap";
import { enqueueSnackbar } from "notistack";
import Swal from "sweetalert2";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import { getDeletedAssets, restoreAsset } from "../../services/ValitanApiService";
import { resolveAssetNameDisplay } from "../../utils/otherValueDisplay";

const CATEGORY_TABS = [
    { label: "All", value: "" },
    { label: "Buildings", value: "BLDG" },
    { label: "Furniture & Fittings", value: "FURN" },
    { label: "ICT Equipment", value: "ICT" },
    { label: "Motor Vehicles", value: "VEH" },
    { label: "Machinery & Plants", value: "MACH" },
    { label: "Land", value: "LAND" },
    { label: "Equipment", value: "EQP" },
    { label: "Infrastructure", value: "INFR" },
    { label: "Site Works", value: "SITE" },
];

const parsePage = (response) => {
    const payload = response?.data?.data ?? response?.data ?? {};
    return {
        content: Array.isArray(payload?.content) ? payload.content : [],
        pageNumber: payload?.number ?? payload?.page ?? 0,
        pageSize: payload?.size ?? 10,
        totalElements: payload?.totalElements ?? 0,
        totalPages: payload?.totalPages ?? 0,
    };
};

const formatDeletedAt = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
};

const DeletedAssets = () => {
    const [activeCategory, setActiveCategory] = useState("");
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const [paginationInfo, setPaginationInfo] = useState({
        pageNumber: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0,
    });

    const fetchDeletedAssets = useCallback(async (pageNumber = 0, pageSize = 10) => {
        setLoading(true);
        try {
            const response = await getDeletedAssets({
                categoryCode: activeCategory || undefined,
                page: pageNumber,
                size: pageSize,
            });
            const result = parsePage(response);
            setAssets(result.content);
            setPaginationInfo({
                pageNumber: result.pageNumber,
                pageSize: result.pageSize,
                totalElements: result.totalElements,
                totalPages: result.totalPages,
            });
        } catch (error) {
            console.error("Failed to load deleted assets:", error);
            enqueueSnackbar("Failed to load deleted assets.", { variant: "error" });
            setAssets([]);
        } finally {
            setLoading(false);
        }
    }, [activeCategory]);

    useEffect(() => {
        fetchDeletedAssets(0, paginationInfo.pageSize);
    }, [activeCategory, fetchDeletedAssets, paginationInfo.pageSize]);

    const handleRestore = async (asset) => {
        if (!asset?.id) return;
        const assetName = resolveAssetNameDisplay(asset);
        const confirmation = await Swal.fire({
            title: "Restore Asset?",
            text: `"${assetName}" will be restored and shown again in asset lists.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Restore",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#0ab39c",
        });
        if (!confirmation.isConfirmed) return;

        setRestoringId(asset.id);
        try {
            await restoreAsset(asset.id);
            enqueueSnackbar("Asset restored successfully.", { variant: "success" });
            await fetchDeletedAssets(paginationInfo.pageNumber, paginationInfo.pageSize);
        } catch (error) {
            console.error("Failed to restore asset:", error);
            enqueueSnackbar("Failed to restore asset.", { variant: "error" });
        } finally {
            setRestoringId(null);
        }
    };

    const displayPaginationInfo = useMemo(
        () => ({
            pageNumber: paginationInfo.pageNumber,
            pageSize: paginationInfo.pageSize,
            totalElements: paginationInfo.totalElements,
            totalPages: paginationInfo.totalPages,
        }),
        [paginationInfo]
    );

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Valuations" breadcrumbItem="Deleted Assets" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <h4 className="card-title mb-0">Deleted Assets</h4>
                                <span className="text-muted">
                                    Soft-deleted assets across all categories
                                </span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    {CATEGORY_TABS.map((tab) => (
                                        <Button
                                            key={tab.value || "ALL"}
                                            size="sm"
                                            color={activeCategory === tab.value ? "primary" : "light"}
                                            onClick={() => setActiveCategory(tab.value)}
                                        >
                                            {tab.label}
                                        </Button>
                                    ))}
                                </div>

                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner size="sm" className="me-2" />
                                        Loading deleted assets...
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Asset Name</th>
                                                    <th>Category</th>
                                                    <th>Barcode</th>
                                                    <th>Institution</th>
                                                    <th>Deleted At</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assets.length > 0 ? (
                                                    assets.map((asset, index) => {
                                                        const categoryCode =
                                                            asset?.asset_category?.code ||
                                                            asset?.assetCategory?.code ||
                                                            "-";
                                                        const categoryName =
                                                            asset?.asset_category?.name ||
                                                            asset?.assetCategory?.name ||
                                                            categoryCode;
                                                        return (
                                                            <tr key={asset.id}>
                                                                <td>
                                                                    {paginationInfo.pageNumber *
                                                                        paginationInfo.pageSize +
                                                                        index +
                                                                        1}
                                                                </td>
                                                                <td>{resolveAssetNameDisplay(asset)}</td>
                                                                <td>{categoryName}</td>
                                                                <td>{asset.barcode || "-"}</td>
                                                                <td>{asset.institution_name || asset.institutionName || "-"}</td>
                                                                <td>{formatDeletedAt(asset.deleted_at || asset.deletedAt)}</td>
                                                                <td>
                                                                    <Button
                                                                        color="success"
                                                                        size="sm"
                                                                        onClick={() => handleRestore(asset)}
                                                                        disabled={String(restoringId) === String(asset.id)}
                                                                        title="Restore asset"
                                                                    >
                                                                        <i className={String(restoringId) === String(asset.id) ? "ri-loader-4-line" : "ri-arrow-go-back-line"} />
                                                                        {" "}
                                                                        Restore
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={7} className="text-center text-muted py-4">
                                                            No deleted assets found.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <Pagination
                                    paginationInfo={displayPaginationInfo}
                                    handleNextPage={() =>
                                        fetchDeletedAssets(
                                            paginationInfo.pageNumber + 1,
                                            paginationInfo.pageSize
                                        )
                                    }
                                    handlePreviousPage={() =>
                                        fetchDeletedAssets(
                                            Math.max(paginationInfo.pageNumber - 1, 0),
                                            paginationInfo.pageSize
                                        )
                                    }
                                    onPageChange={(pageNumber) =>
                                        fetchDeletedAssets(pageNumber, paginationInfo.pageSize)
                                    }
                                    onPageSizeChange={(pageSize) => fetchDeletedAssets(0, pageSize)}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default DeletedAssets;
